import type { WarningNotice } from '@/pages/accounting/statements/statementMeta';
import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Button } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { StatementWarnings } from '@/pages/accounting/statements/components';
import { isoToDisplayDate } from '@/pages/accounting/statements/statementMeta';
import { VatBreakdownTable, VatSettlement, VatSummary } from './components';
import type { VatDrillTarget, VatRow } from './vatMeta';
import type { UseVatReportPageResult } from './useVatReportPage';

type VatReportPageViewProps = {
    /** The window the figures cover, as the server echoed it. */
    reportedFrom: string;
    reportedTo: string;
    /** The month a return filed today covers, and when it is due (BOS-166). */
    filingFrom: string;
    filingTo: string;
    filingDueDate: string;
    filingMismatch: { from: string; to: string } | null;
    onShowFilingPeriod: () => void;
    /** `null` while the report is in flight — labels stay, values skeleton. */
    summary: UseVatReportPageResult['summary'];
    isLoading: boolean;
    outputRows: VatRow[];
    inputRows: VatRow[];
    outputDrillTargets: VatDrillTarget[];
    inputDrillTargets: VatDrillTarget[];
    warningNotices: WarningNotice[];
    onDismissWarnings: () => void;
    onViewLedger: (accountId: string) => void;
    onSettled: () => void;
    generatedText: string;
};

/**
 * VAT report — render only. The Books shell supplies the page title **and the
 * period**, so unlike its `/accounting` counterpart this carries no range
 * control and no presets. What is left is the summary payoff, the settlement
 * action, advisory notices, and the two-sided breakdown.
 */
export const VatReportPageView: React.FC<VatReportPageViewProps> = ({
    reportedFrom,
    reportedTo,
    filingFrom,
    filingTo,
    filingDueDate,
    filingMismatch,
    onShowFilingPeriod,
    summary,
    isLoading,
    outputRows,
    inputRows,
    outputDrillTargets,
    inputDrillTargets,
    warningNotices,
    onDismissWarnings,
    onViewLedger,
    onSettled,
    generatedText,
}) => {
    const { t } = useTranslation('pages');
    const periodText = `${isoToDisplayDate(reportedFrom)} – ${isoToDisplayDate(reportedTo)}`;
    const filingPeriodText = `${isoToDisplayDate(filingFrom)} – ${isoToDisplayDate(filingTo)}`;

    return (
        <section aria-label={t('vat.title')} className="flex flex-col gap-4">
            {/* Print is all that is left of the control row — the period now
                belongs to the Books shell above. */}
            <div className="flex flex-wrap items-end justify-end gap-4 print:hidden">
                <Button
                    isDisabled={isLoading}
                    onPress={() => window.print()}
                    variant="outline"
                    {...getNodeTestIdProp('vat-print')}
                >
                    <Icon aria-hidden icon="gravity-ui:printer" />
                    {t('statements.print')}
                </Button>
            </div>

            {/* The period the figures cover, kept for print — where the range
                control above is hidden and the page would otherwise carry no
                statement of what it reports on. */}
            <p
                className="hidden text-sm text-muted print:block"
                {...getNodeTestIdProp('vat-period-print')}
            >
                {t('vat.settlement.period', { period: periodText })}
            </p>

            {/* BOS-166: which period this is, in words, and a way back to the
                one actually being filed. The tab opens on the month a return
                filed today covers — always the previous one — so a shell window
                of month-to-date no longer shows zeros and a disabled settle
                button to an accountant who came here to settle August. */}
            <p
                className="text-sm text-muted"
                {...getNodeTestIdProp('vat-filing-note')}
            >
                {t('vat.filing.showing', {
                    period: filingPeriodText,
                    due: isoToDisplayDate(filingDueDate),
                })}
                {filingMismatch ? (
                    <Button
                        className="ms-2 align-baseline"
                        onPress={onShowFilingPeriod}
                        size="sm"
                        variant="ghost"
                        {...getNodeTestIdProp('vat-filing-switch')}
                    >
                        {t('vat.filing.switch')}
                    </Button>
                ) : null}
            </p>

            <StatementWarnings
                notices={warningNotices}
                onDismiss={onDismissWarnings}
                testIdPrefix="vat"
            />

            <VatSummary
                direction={summary?.direction ?? null}
                inputText={summary?.inputText ?? null}
                netText={summary?.netText ?? null}
                outputText={summary?.outputText ?? null}
            />

            <VatSettlement
                fromDate={reportedFrom}
                netTetri={summary?.netTetri ?? 0}
                onSettled={onSettled}
                onViewLedger={onViewLedger}
                periodText={periodText}
                settlementAccountCode={summary?.settlementAccountCode ?? null}
                toDate={reportedTo}
            />

            <div className="grid gap-4 lg:grid-cols-2">
                <VatBreakdownTable
                    ariaLabel={t('vat.breakdown.outputAria')}
                    emptyText={t('vat.breakdown.noActivity')}
                    isLoading={isLoading}
                    onViewLedger={onViewLedger}
                    rows={outputRows}
                    testIdPrefix="vat-output"
                    title={t('vat.summary.output')}
                    totalDrillTargets={outputDrillTargets}
                    totalText={summary?.outputText ?? null}
                />
                <VatBreakdownTable
                    ariaLabel={t('vat.breakdown.inputAria')}
                    emptyText={t('vat.breakdown.noActivity')}
                    isLoading={isLoading}
                    onViewLedger={onViewLedger}
                    rows={inputRows}
                    testIdPrefix="vat-input"
                    title={t('vat.summary.input')}
                    totalDrillTargets={inputDrillTargets}
                    totalText={summary?.inputText ?? null}
                />
            </div>

            <div className="mt-1 flex flex-wrap justify-between gap-2 px-1 text-xs text-muted">
                <span>{t('statements.footer.currencyNote')}</span>
                <span {...getNodeTestIdProp('vat-generated')}>
                    {t('statements.footer.generated', {
                        datetime: generatedText,
                    })}
                </span>
            </div>
        </section>
    );
};
